import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

export function useNavCounts() {
  return useQuery({
    queryKey: ['nav-counts'],
    queryFn: () => apiClient.get('/notifications/nav-counts'),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: () => apiClient.get('/notifications'),
    refetchInterval: 30_000, // Poll every 30s for real-time-ish updates
  });
}

export function useMarkAllRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.patch('/notifications/read'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.patch(`/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useBatchMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => apiClient.post('/notifications/batch-read', { ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkSectionRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (section: string) => apiClient.patch('/notifications/read-by-section', { section }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nav-counts'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// ===== Notification Preferences =====

export function useNotificationPreferences() {
  return useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => apiClient.get('/notifications/preferences'),
    staleTime: 60_000,
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (preferences: Array<{ type: string; email: boolean; inApp: boolean }>) =>
      apiClient.put('/notifications/preferences', { preferences }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
  });
}
